import { useFormik } from "formik";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useLeaseCompStore, useMapMarkerStore, usePipedriveStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useLeaseCompDelete from "../../hooks/useLeaseCompDelete";
import useLeaseCompUpdate from "../../hooks/useLeaseCompUpdate";
import useLeaseCompValidationSchema from "../../hooks/useLeaseCompValidationSchema";
import DetailsType from "../../interfaces/DetailsType";
import { ILeaseCompState, LeaseCompState } from "../../interfaces/ILeaseComp";
import { ClosableOrder } from "../../store/ClosableStore";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanel from "../DetailsPanel";
import EditControls, { DeleteControl } from "../EditControls";
import LeaseCompIcon from "../icons/LeaseCompIcon";
import OnMapSpinner from "../spinners/OnMapSpinner";
import LeaseCompFields from "./LeaseCompFields";

function LeaseCompEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const updateLeaseComp = useLeaseCompUpdate();
    const deleteLeaseComp = useLeaseCompDelete();
    const leaseCompStore = useLeaseCompStore();
    const pipedriveStore = usePipedriveStore();
    const mapMarkerStore = useMapMarkerStore();
    const leaseComp = useMemo(() => computed(() => leaseCompStore.leaseComps.find(lc => lc.id.toString() === id)), [id]).get();
    const pipedriveOrganization = useMemo(() => computed(() => leaseComp && pipedriveStore.organizations.find(o => o.id === leaseComp.pipedriveCompanyId)), [leaseComp]).get();
    const validationSchema = useLeaseCompValidationSchema();

    const close = () => navigate("/");
    const cancel = () => navigate(`/leaseComps/${leaseComp!.id}`);
    useClosable(ClosableOrder.Medium, cancel);

    const formik = useFormik<ILeaseCompState>({
        initialValues: LeaseCompState.empty(),
        validationSchema: toFormikValidationSchema(validationSchema),
        onSubmit: async (values) => {
            await updateLeaseComp(leaseComp!, values);
            cancel();
        },
    });

    const remove = async () => {
        await deleteLeaseComp(leaseComp!.id);
        close();
    }

    useEffect(() => {
        if (leaseComp) {
            leaseCompStore.setEditedLeaseCompId(leaseComp.id);
            mapMarkerStore.draw(leaseComp.centerPoint);
            formik.resetForm({
                values: {
                    address: leaseComp.address,
                    signedDate: leaseComp.signedDate,
                    landPsf: leaseComp.landPsf,
                    buildingPsf: leaseComp.buildingPsf,
                    totalRent: leaseComp.totalRent,
                    zoning: leaseComp.zoning,
                    propertyType: leaseComp.propertyType,
                    parcelSizeAcres: leaseComp.parcelSizeAcres,
                    tenantLandSize: leaseComp.tenantLandSize,
                    buildingSize: leaseComp.buildingSize,
                    options: leaseComp.options,
                    pipedriveCompanyName: pipedriveOrganization?.name || null,
                    notes: leaseComp.notes,
                    leaseExpiration: leaseComp.leaseExpiration,
                    type: leaseComp.type,
                    year: leaseComp.year,
                    freeRent: leaseComp.freeRent,
                    ti: leaseComp.ti,
                    escalations: leaseComp.escalations,
                    compSource: leaseComp.compSource,
                },
            });
        }

        return () => {
            leaseCompStore.setEditedLeaseCompId(null);
            mapMarkerStore.clear();
        };
    }, [leaseComp, pipedriveOrganization]);

    if (leaseCompStore.isLeaseCompsLoading.fetching)
        return <OnMapSpinner />;

    if (!leaseComp)
        return null;

    return (
        <DetailsPanel type={DetailsType.LeaseCompEdit} onClose={close}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <LeaseCompIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.primary}>{leaseComp.address}</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <LeaseCompFields formik={formik} />
            </div>
            <div className={commonStyles.separator}></div>
            <div className={commonStyles.controls}>
                <div className={commonStyles.controlGroup}>
                    <EditControls
                        isStateChanged={formik.dirty || !mapMarkerStore.isMatch(leaseComp.centerPoint)}
                        isUpdateLoading={leaseCompStore.isLeaseCompsLoading.updating! || pipedriveStore.isOrganizationsLoading.creating!}
                        onUpdate={formik.handleSubmit}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}>
                    <DeleteControl
                        entityName="Lease Comp"
                        isLoading={leaseCompStore.isLeaseCompsLoading.deleting!}
                        onDelete={remove}
                    />
                </div>
            </div>
        </DetailsPanel>
    );
}

export default observer(LeaseCompEdit);
